import React, { useState, useRef, useEffect } from 'react';
import './MobileNoteInput.css';

function MobileNoteInput({
  isOpen,
  onClose,
  donorName,
  onSubmit
}) {
  const [noteText, setNoteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const textareaRef = useRef(null);

  // Auto-focus when opening
  useEffect(() => {
    if (isOpen) {
      setNoteText('');
      setConfirmation(null);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Auto-resize textarea
  const handleTextChange = (e) => {
    setNoteText(e.target.value);
    // Reset height to auto to get the correct scrollHeight
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
  };

  const handleSubmit = async () => {
    if (!noteText.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const fullMessage = `${donorName}: ${noteText}`;
      const result = await onSubmit(fullMessage);

      setConfirmation({
        text: noteText,
        captured: result?.captured || [
          { type: 'logged', text: 'Note saved' }
        ]
      });

    } catch (error) {
      console.error('Error submitting note:', error);
      setConfirmation({
        text: noteText,
        captured: [{ type: 'logged', text: 'Note saved' }]
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    setNoteText('');
    setConfirmation(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-note-overlay">
      <div className="mobile-note-container">

        {/* Minimal header */}
        <div className="mobile-note-header">
          <button className="mobile-note-cancel" onClick={handleDone}>
            Cancel
          </button>
          <span className="mobile-note-title">Add Note</span>
          <div style={{ width: '50px' }} />
        </div>

        {/* Content */}
        <div className="mobile-note-content">

          {confirmation ? (
            /* Confirmation state */
            <div className="mobile-note-confirmation">
              <div className="confirmation-card">
                <div className="confirmation-text">
                  <span className="confirmation-name">{donorName}:</span> {confirmation.text}
                </div>

                <div className="captured-section">
                  <div className="captured-label">Captured</div>
                  <div className="captured-items">
                    {confirmation.captured.map((item, idx) => (
                      <div key={idx} className="captured-item">
                        <span className={`captured-icon ${item.type}`}>
                          {item.type === 'task' ? '→' : '✓'}
                        </span>
                        <span className="captured-text">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Input bar placeholder at bottom */}
              <div className="confirmation-input-bar" onClick={handleDone}>
                <span>What happened?</span>
                <div className="send-circle">
                  <span>↑</span>
                </div>
              </div>
            </div>
          ) : (
            /* Input state - at bottom */
            <>
              {/* Input box */}
              <div className="mobile-note-input-area">
                <div className="note-input-content">
                  <span className="note-name">{donorName}:</span>
                  <textarea
                    ref={textareaRef}
                    className="note-textarea"
                    value={noteText}
                    onChange={handleTextChange}
                    placeholder="what happened?"
                    rows={1}
                  />
                </div>
              </div>

              {/* Keyboard bar */}
              <div className="mobile-note-keyboard-bar">
                <button className="mic-button">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>
                <button
                  className={`done-button ${noteText.trim() ? 'active' : ''}`}
                  onClick={handleSubmit}
                  disabled={!noteText.trim() || isSubmitting}
                >
                  {isSubmitting ? '...' : 'Done'}
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default MobileNoteInput;
